import useDebounce from 'common/hooks/useDebounce';
import { useGetProspectActivities } from 'common/queries-and-mutations/admin/prospect';
import Button from 'components/Form/Button';
import SearchInput from 'components/Form/SearchInput';
import { Icon, Icons } from 'components/Icon';
import { Loader } from 'components/Loader';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { truncateText } from 'utils';
import NoteViewModal from './NoteViewModal';
import withCreatePortal from 'components/HOC/withCreatePortal';
import { Activity } from 'service/dto/prospect.dto';

const EnhancedNoteViewModal = withCreatePortal(NoteViewModal);

export default function Notes() {
  const { prospectId } = useParams();
  const [urlParams, setUrlParams] = useSearchParams();
  const searchQueryString = urlParams.get('search_query') ?? '';
  const debounceSearch = useDebounce(searchQueryString, 1300);
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const [viewNote, setViewNote] = useState<Activity | null>(null);

  const { data, isLoading, isFetched } = useGetProspectActivities({
    prospectId: prospectId!
  });

  useEffect(() => {
    if (debounceSearch.length > 3) {
      urlParams.set('page', '1');
      urlParams.set('items_per_page', '10');
      setUrlParams(urlParams);
      setIsExecutingSearch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearch.length]);

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    if (value.trim() === '') {
      urlParams.delete(name);
      urlParams.set('page', '1');
      urlParams.set('items_per_page', '10');
      setUrlParams(urlParams);
    }
    if (value.trim()) {
      urlParams.set(name, value);
      urlParams.set('page', '1');
      urlParams.set('items_per_page', '10');
      setUrlParams(urlParams);
    }
  };

  const activities = useMemo(() => {
    if (isExecutingSearch) {
      return (data?.activities ?? []).filter(
        item =>
          item.narration
            .toLowerCase()
            .includes(searchQueryString.toLowerCase()) ||
          item.createdDate
            .toLowerCase()
            .includes(searchQueryString.toLowerCase())
      );
    }
    return data?.activities;
  }, [data?.activities, isExecutingSearch, searchQueryString]);

  if (isLoading && isFetched) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <Breadcrumb title="Make a Note" />
        <Link to={`/admin/prospects/create-note/${prospectId}`}>
          <Button
            variant="secondary"
            size="custom"
            className="h-12 w-[10rem] rounded-[3px] border-[#FF6600] bg-[#FF66061A] p-4 text-sm font-medium text-[#FF6606]"
            label="Add Note"
            type="button"
          ></Button>
        </Link>
      </div>

      <div className="mt-5 flex flex-col">
        <div className="h-fit w-full rounded-md bg-white pb-28 shadow-sm">
          <div className="border-b border-[#E5E5E5]">
            <div className="flex items-center justify-between p-3 px-5">
              <span className="text-xl font-medium text-[#353535]">
                Notes list
              </span>
              <div className="flex items-center gap-3">
                <SearchInput
                  name="search_query"
                  placeholder="Search for note"
                  value={searchQueryString}
                  onChange={handleChange}
                  size="lg"
                  className="h-11 w-[20.1rem] rounded-[3px] border border-[#C3C3C3] bg-white"
                />
              </div>
            </div>
          </div>

          <table className="w-full text-left text-sm">
            <tbody className="px-6 py-4">
              {!activities || activities.length < 1 ? (
                <tr className="text-center">
                  <td colSpan={5} className="py-10 text-lg">
                    No results found
                  </td>
                </tr>
              ) : (
                (activities ?? [])?.map(activity => (
                  <tr
                    className="border-b bg-white hover:bg-gray-50"
                    key={activity.prospectActivity_id}
                  >
                    <td className="w-[54%] p-4 text-xs font-normal text-[#353535]">
                      <div className="flex items-center gap-3 py-3">
                        <Icon name={Icons.Note} />
                        <span className="text-xs font-normal text-[#353535]">
                          {truncateText(activity.narration, 100)}
                        </span>
                      </div>
                    </td>
                    <td className="w-[39%] px-6 py-4 text-xs font-normal text-[#353535]">
                      {activity.createdDate}
                    </td>

                    <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                      <div>
                        <button
                          type="button"
                          className="w-20 rounded-md bg-primary-900 p-2 text-white"
                          onClick={() => setViewNote(activity)}
                        >
                          View Note
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {viewNote && (
        <EnhancedNoteViewModal
          note={viewNote}
          onClose={() => setViewNote(null)}
        />
      )}
    </div>
  );
}
